/* ============================================================
   SHINE & STYLE — navigation
   Sticky header, desktop mega menu, mobile drawer, footer
   accordions. No dependencies.

   Everything here degrades: without JS the header is a plain
   sticky bar, the mega menus stay closed, and the footer columns
   render open (the accordion CSS only applies under `.js`).
   ============================================================ */
(function () {
  'use strict';

  var DESKTOP = window.matchMedia('(min-width: 900px)');
  var root = document.documentElement;

  root.classList.add('js');

  /* ---------------------------------------------------------
     Sticky header
     Compacts past 20px. Hides on downward scroll once we are
     clear of the hero, so a long grid gets the viewport back.
     Reads scroll in a rAF — a raw scroll handler that touches
     offsetHeight forces layout on every frame of a flick.
     --------------------------------------------------------- */
  var header = document.querySelector('[data-header]');
  if (header) {
    var lastY = window.scrollY;
    var ticking = false;

    var onScroll = function () {
      var y = window.scrollY;

      header.classList.toggle('is-stuck', y > 20);

      // Never hide while a menu is open, or the drawer's trigger
      // scrolls out from under the user's finger.
      var menuOpen = document.body.classList.contains('ss-locked') ||
                     !!header.querySelector('.ss-nav__item.is-open');

      if (!menuOpen && y > 400) {
        header.classList.toggle('is-hidden', y > lastY + 4);
      } else {
        header.classList.remove('is-hidden');
      }

      lastY = y;
      ticking = false;
    };

    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; window.requestAnimationFrame(onScroll); }
    }, { passive: true });

    onScroll();
  }

  /* ---------------------------------------------------------
     Desktop mega menu
     Hover opens, but with a close delay so a diagonal path from
     the trigger down into the panel does not snap it shut.
     Focus opens too, so it is reachable by keyboard.
     --------------------------------------------------------- */
  var megaItems = Array.prototype.slice.call(document.querySelectorAll('[data-mega-item]'));

  function closeAllMega(except) {
    megaItems.forEach(function (item) {
      if (item === except) return;
      item.classList.remove('is-open');
      var trigger = item.querySelector('[data-mega-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    });
  }

  megaItems.forEach(function (item) {
    var trigger = item.querySelector('[data-mega-trigger]');
    var closeTimer;

    function open() {
      if (!DESKTOP.matches) return;
      window.clearTimeout(closeTimer);
      closeAllMega(item);
      item.classList.add('is-open');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
    }

    function scheduleClose() {
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(function () {
        item.classList.remove('is-open');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      }, 180);
    }

    item.addEventListener('mouseenter', open);
    item.addEventListener('mouseleave', scheduleClose);
    item.addEventListener('focusin', open);
    item.addEventListener('focusout', function (e) {
      // Only close once focus has actually left the whole item.
      if (!item.contains(e.relatedTarget)) scheduleClose();
    });

    // On a touch screen the first tap opens rather than navigating.
    if (trigger) {
      trigger.addEventListener('click', function (e) {
        if (!DESKTOP.matches) return;
        if (!item.classList.contains('is-open')) {
          e.preventDefault();
          open();
        }
      });
    }
  });

  /* ---------------------------------------------------------
     Mobile drawer
     --------------------------------------------------------- */
  var drawer = document.querySelector('[data-drawer]');
  var overlay = document.querySelector('[data-overlay]');
  var burger = document.querySelector('[data-burger]');
  var lastFocus = null;

  function openDrawer() {
    if (!drawer) return;
    lastFocus = document.activeElement;
    drawer.classList.add('is-open');
    drawer.removeAttribute('inert');
    if (overlay) overlay.classList.add('is-open');
    if (burger) burger.setAttribute('aria-expanded', 'true');
    document.body.classList.add('ss-locked');

    var first = drawer.querySelector('[data-drawer-close]');
    if (first) first.focus();
  }

  function closeDrawer() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('inert', '');
    if (overlay) overlay.classList.remove('is-open');
    if (burger) burger.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('ss-locked');
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  if (burger) {
    burger.addEventListener('click', function () {
      if (drawer && drawer.classList.contains('is-open')) closeDrawer();
      else openDrawer();
    });
  }
  if (overlay) overlay.addEventListener('click', closeDrawer);

  Array.prototype.forEach.call(
    document.querySelectorAll('[data-drawer-close]'),
    function (el) { el.addEventListener('click', closeDrawer); }
  );

  // Start closed and inert so its links are skipped by the tab order.
  if (drawer && !drawer.classList.contains('is-open')) drawer.setAttribute('inert', '');

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    if (drawer && drawer.classList.contains('is-open')) { closeDrawer(); return; }
    closeAllMega(null);
  });

  // Keep focus inside the drawer while it is open.
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab' || !drawer || !drawer.classList.contains('is-open')) return;

    var focusables = drawer.querySelectorAll(
      'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return;

    var first = focusables[0];
    var last = focusables[focusables.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  });

  // Resizing up to desktop with the drawer open would leave the
  // body locked behind a hidden panel.
  var onBreakpoint = function (e) { if (e.matches) closeDrawer(); else closeAllMega(null); };
  if (DESKTOP.addEventListener) DESKTOP.addEventListener('change', onBreakpoint);
  else if (DESKTOP.addListener) DESKTOP.addListener(onBreakpoint);

  /* ---------------------------------------------------------
     Accordions — drawer sub-menus and mobile footer columns.
     One handler for both; they differ only in which element
     carries the open class.
     --------------------------------------------------------- */
  Array.prototype.forEach.call(
    document.querySelectorAll('[data-accordion]'),
    function (btn) {
      btn.addEventListener('click', function () {
        var openNow = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!openNow));

        var targetSel = btn.getAttribute('data-accordion');
        var target = targetSel ? document.getElementById(targetSel) : null;
        if (target) target.classList.toggle('is-open', !openNow);

        var col = btn.closest('[data-accordion-parent]');
        if (col) col.classList.toggle('is-open', !openNow);
      });
    }
  );

  /* ---------------------------------------------------------
     Cart count bump — demo hook for the prototype. Quick-add
     buttons increment the badge so the interaction is visible.
     The real theme replaces this with the Shopify cart event.
     --------------------------------------------------------- */
  var count = document.querySelector('[data-cart-count]');
  Array.prototype.forEach.call(
    document.querySelectorAll('[data-quick-add]'),
    function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        if (!count) return;
        count.textContent = String((parseInt(count.textContent, 10) || 0) + 1);
        count.classList.remove('is-bump');
        void count.offsetWidth;          // restart the animation
        count.classList.add('is-bump');

        var original = btn.textContent;
        btn.textContent = 'Added';
        window.setTimeout(function () { btn.textContent = original; }, 1200);
      });
    }
  );

  /* Wishlist toggle — visual only in the prototype. */
  Array.prototype.forEach.call(
    document.querySelectorAll('[data-wish]'),
    function (btn) {
      btn.addEventListener('click', function () {
        btn.setAttribute('aria-pressed', btn.getAttribute('aria-pressed') === 'true' ? 'false' : 'true');
      });
    }
  );
})();
